import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Download, ChevronDown, FileText, Zap, BookOpen, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useSpace } from '../context/SpaceContext';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Plan → allowed export formats (mirrors backend PLAN_EXPORT_RIGHTS)
const PLAN_RIGHTS = {
  free: [],
  explorateur: [],
  solo: ['pdf', 'pptx'],
  famille: ['pdf', 'pptx'],
  organisation: ['pdf', 'pptx', 'scorm'],
  entreprise: ['pdf', 'pptx', 'scorm', 'xapi'],
  createur_pro: ['pdf', 'pptx', 'scorm'],
  admin: ['pdf', 'pptx', 'scorm', 'xapi'],
};

function canExportFmt(plan, fmt) {
  return (PLAN_RIGHTS[plan] || []).includes(fmt);
}

// ── Portal Dropdown ──────────────────────────────────────────────────────────
function ExportDropdownPortal({ anchorRef, open, onClose, onExport, loading, effectivePlan }) {
  const [style, setStyle] = useState({});

  useEffect(() => {
    if (!open || !anchorRef.current) return;

    const reposition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const DROPDOWN_HEIGHT = 280;
      const GAP = 6;
      const openUpward = (window.innerHeight - rect.bottom) < DROPDOWN_HEIGHT + GAP;

      setStyle({
        position: 'fixed',
        right: window.innerWidth - rect.right,
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + GAP }
          : { top: rect.bottom + GAP }),
        zIndex: 99999,
        minWidth: '280px',
      });
    };

    reposition();
    window.addEventListener('scroll', onClose, { passive: true });
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', onClose);
      window.removeEventListener('resize', reposition);
    };
  }, [open, anchorRef, onClose]);

  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (anchorRef.current && anchorRef.current.contains(e.target)) return;
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
      onClose();
    };
    const keyHandler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [open, anchorRef, onClose]);

  if (!open) return null;

  const hasPdf   = canExportFmt(effectivePlan, 'pdf');
  const hasPptx  = canExportFmt(effectivePlan, 'pptx');
  const hasScorm = canExportFmt(effectivePlan, 'scorm');
  const hasXapi  = canExportFmt(effectivePlan, 'xapi');

  return ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      data-testid="export-dropdown"
      style={style}
      className="bg-[#1a1927] border border-white/10 rounded-2xl shadow-2xl shadow-black/80 overflow-hidden animate-fade-up"
    >
      <div className="px-4 pt-3 pb-1">
        <p className="text-[10px] font-semibold text-[#a0a0ab] uppercase tracking-wider">Document</p>
      </div>

      <ExportOption
        icon={FileText}
        label="Télécharger en PDF"
        sub="Document imprimable multi-pages"
        onClick={() => onExport('pdf')}
        loading={loading === 'pdf'}
        locked={!hasPdf}
        lockMsg="Plan Solo requis"
        testId="export-pdf-btn"
      />
      <ExportOption
        icon={BookOpen}
        label="Télécharger en PowerPoint"
        sub="Présentation modifiable .pptx"
        onClick={() => onExport('pptx')}
        loading={loading === 'pptx'}
        locked={!hasPptx}
        lockMsg="Plan Solo requis"
        testId="export-pptx-btn"
      />

      <div className="border-t border-white/5 mx-4 my-1" />

      <div className="px-4 pt-2 pb-1">
        <p className="text-[10px] font-semibold text-[#a0a0ab] uppercase tracking-wider">e-Learning</p>
      </div>
      <ExportOption
        icon={Zap}
        label="Exporter SCORM 1.2"
        sub={hasScorm ? "Package offline compatible LMS" : "Plan Organisation requis"}
        onClick={() => onExport('scorm')}
        loading={loading === 'scorm'}
        locked={!hasScorm}
        lockMsg="Plan Organisation requis"
        testId="export-scorm-btn"
      />
      <ExportOption
        icon={Zap}
        label="Exporter xAPI (Tin Can)"
        sub={hasXapi ? "Suivi avancé multi-plateforme" : "Plan Entreprise requis"}
        onClick={() => onExport('xapi')}
        loading={loading === 'xapi'}
        locked={!hasXapi}
        lockMsg="Plan Entreprise requis"
        testId="export-xapi-btn"
      />
      <div className="h-2" />
    </div>,
    document.body
  );
}

// ── Export Option Row ────────────────────────────────────────────────────────
function ExportOption({ icon: Icon, label, sub, onClick, loading, locked, lockMsg, testId }) {
  return (
    <button
      onClick={locked ? undefined : onClick}
      disabled={!!loading || locked}
      data-testid={testId}
      title={locked ? lockMsg : undefined}
      className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left ${locked ? 'opacity-45 cursor-not-allowed' : 'hover:bg-white/8'}`}
    >
      <div className="w-7 h-7 rounded-lg bg-white/8 flex items-center justify-center shrink-0">
        {loading
          ? <Loader2 size={13} className="animate-spin text-[#a0a0ab]" />
          : locked
          ? <Lock size={12} className="text-[#a0a0ab]" />
          : <Icon size={13} className="text-[#ff6b6b]" />
        }
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-white text-sm font-medium leading-tight">{label}</p>
        {sub && <p className={`text-xs ${locked ? 'text-[#ff6b6b]/70' : 'text-[#a0a0ab]'}`}>{locked ? lockMsg : sub}</p>}
      </div>
    </button>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function ExportButton({ courseId, courseTitle }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(null);
  const btnRef = useRef(null);
  const { activeSpace } = useSpace();
  const { user } = useAuth();

  const close = useCallback(() => setOpen(false), []);
  const handleToggle = () => setOpen(prev => !prev);

  // Effective plan: space type when in a space, user's plan otherwise
  const effectivePlan = activeSpace?.type || user?.plan || 'free';

  const handleExport = async (format) => {
    setOpen(false);
    setLoading(format);

    try {
      const spaceParam = activeSpace?.id ? `?space_id=${activeSpace.id}` : '';
      const resp = await fetch(
        `${API}/api/export/${courseId}/${format}${spaceParam}`,
        { method: 'POST', credentials: 'include' }
      );

      if (!resp.ok) {
        let message = "Erreur lors de l'export.";
        try {
          const errData = await resp.json();
          message = errData?.detail || message;
        } catch { /* use default */ }

        if (resp.status === 403) {
          toast.error(message, { description: 'Mettez à niveau votre abonnement pour accéder à ce format.' });
        } else if (resp.status === 404) {
          toast.error('Cours introuvable. Rechargez la page.');
        } else {
          toast.error(message);
        }
        return;
      }

      const blob = await resp.blob();
      const ext = { pdf: 'pdf', pptx: 'pptx', scorm: 'zip', xapi: 'zip' }[format] ?? format;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(courseTitle || 'cours')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase()}.${ext}`;

      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 150);

      toast.success(`Export ${format.toUpperCase()} téléchargé avec succès`);
    } catch (err) {
      toast.error("Erreur lors de l'export.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div ref={btnRef} className="relative">
      <button
        onClick={handleToggle}
        data-testid="export-btn"
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm bg-white/8 hover:bg-white/12 border border-white/10 text-white transition-colors"
      >
        {loading
          ? <Loader2 size={13} className="animate-spin" />
          : <Download size={13} />
        }
        <span className="hidden sm:inline">Exporter</span>
        <ChevronDown
          size={11}
          className={`text-[#a0a0ab] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <ExportDropdownPortal
        anchorRef={btnRef}
        open={open}
        onClose={close}
        onExport={handleExport}
        loading={loading}
        effectivePlan={effectivePlan}
      />
    </div>
  );
}
